import { useEffect, useMemo, useState } from 'react';
import { getPolicyVersion, getPolicyVersions } from '../../api/policy/policy.api';
import { parsePolicyText } from '@repo/common';
import {
  POLICIES,
  formatPolicyDate,
  policyDateLabel,
  policyLocale,
} from './policyRegistry';

// 코드에 들어 있는 버전과 어드민이 등록한 버전을 시행일·언어별로 합친다. 시행 여부는 서버 기준 오늘(KST)로 가른다
export const POLICY_LANGS = ['ko', 'en'];

const localToday = () => {
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

export const availableLangs = (version) =>
  version ? POLICY_LANGS.filter((lang) => version.languages[lang]) : [];

export const mergePolicyVersions = (policyKey, remote) => {
  const today = remote?.today || localToday();
  const byDate = new Map();
  const entryFor = (effectiveDate, source) => {
    if (!byDate.has(effectiveDate)) {
      byDate.set(effectiveDate, { effectiveDate, source, languages: {} });
    }
    return byDate.get(effectiveDate);
  };

  (POLICIES[policyKey]?.versions ?? []).forEach((doc) => {
    const lang = doc.lang || policyLocale(policyKey);
    entryFor(doc.effectiveDate, 'bundled').languages[lang] = {
      title: doc.title,
      lastUpdated: doc.lastUpdated,
      document: doc,
    };
  });

  (remote?.versions ?? []).forEach((version) => {
    if (byDate.has(version.effectiveDate)) return;
    const entry = entryFor(version.effectiveDate, 'admin');
    entry.id = version.id;
    entry.updatedAt = version.updatedAt;
    if (version.titleKo) {
      entry.languages.ko = {
        title: version.titleKo,
        lastUpdated: formatPolicyDate(version.effectiveDate, 'ko'),
      };
    }
    if (version.titleEn) {
      entry.languages.en = {
        title: version.titleEn,
        lastUpdated: formatPolicyDate(version.effectiveDate, 'en'),
      };
    }
  });

  const all = [...byDate.values()].sort((a, b) =>
    a.effectiveDate.localeCompare(b.effectiveDate),
  );
  const effective = all.filter((version) => version.effectiveDate <= today);
  const upcoming = all.filter((version) => version.effectiveDate > today);

  return {
    today,
    all,
    effective,
    upcoming,
    current: effective[effective.length - 1] ?? null,
  };
};

export const usePolicyVersions = (policyKey) => {
  const [remote, setRemote] = useState({ key: null, data: null });

  useEffect(() => {
    let cancelled = false;
    getPolicyVersions(policyKey)
      .then((data) => !cancelled && setRemote({ key: policyKey, data }))
      .catch(() => !cancelled && setRemote({ key: policyKey, data: null }));
    return () => {
      cancelled = true;
    };
  }, [policyKey]);

  const loaded = remote.key === policyKey;
  const merged = useMemo(
    () => mergePolicyVersions(policyKey, loaded ? remote.data : null),
    [policyKey, loaded, remote.data],
  );
  return { loaded, ...merged };
};

export const usePolicyDocument = (policyKey, version, lang) => {
  const [registered, setRegistered] = useState({ key: null, data: null });
  const cacheKey =
    version?.source === 'admin' ? `${policyKey}:${version.effectiveDate}` : null;

  useEffect(() => {
    if (!cacheKey) return undefined;
    let cancelled = false;
    getPolicyVersion(policyKey, version.effectiveDate)
      .then((data) => !cancelled && setRegistered({ key: cacheKey, data }))
      .catch(() => !cancelled && setRegistered({ key: cacheKey, data: null }));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  const document = useMemo(() => {
    if (!version) return null;
    if (version.source !== 'admin') return version.languages[lang]?.document ?? null;
    if (registered.key !== cacheKey || !registered.data) return null;

    const data = registered.data;
    const title = lang === 'ko' ? data.titleKo : data.titleEn;
    const body = lang === 'ko' ? data.bodyKo : data.bodyEn;
    if (!body) return null;
    return {
      title,
      lastUpdated: formatPolicyDate(data.effectiveDate, lang),
      lastUpdatedLabel: policyDateLabel(lang, false),
      effectiveDate: data.effectiveDate,
      sections: parsePolicyText(body),
    };
  }, [version, lang, registered, cacheKey]);

  const loaded = !version || version.source !== 'admin' || registered.key === cacheKey;
  return { loaded, document };
};
